import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {ArtworkService} from "../../../services/artwork/artwork.service";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {Artwork} from "../../../models/artwork";
import {CustomValidators} from "../../../calsses/CustomValidators";
import {combineLatest, forkJoin, merge} from "rxjs";
import {Media} from "../../../models/media";
import {MediaService} from "../../../services/media/media.service";
import {Category} from "../../../models/category";
import {Group} from "../../../models/group";
import {environment} from '../../../../environments/environment';
import {DatabaseService} from "../../../services/database/database.service";
import {GroupService} from "../../../services/group/group.service";
import {MessagesService} from "../../../services/messages/messages.service";
import {CategoryService} from "../../../services/category/category.service";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {ArtworkCategoryModal} from "../../../modals/artwork-category-modal/artwork-category-modal.component";
import {ArtworkCategory} from '../../../models/artwork-category';
import {AuthService} from "../../../services/auth/auth.service";
import {Route} from "../../../models/route";
import {RouteService} from "../../../services/route/route.service";

@Component({
  selector: 'app-edit-artwork',
  templateUrl: './edit-artwork.component.html',
  styleUrls: ['./edit-artwork.component.scss']
})
export class EditArtworkComponent implements OnInit {

  code: string;
  groups: Array<Group>;
  routes: Array<Route>;
  artwork: Artwork;
  categories: Array<Category> = [];
  mediaImages: Array<Media> = [];
  mediaVideos: Array<Media> = [];
  mediaAudios: Array<Media> = [];
  assignedCategories: Array<Category> = [];

  environment = environment;

  projectId: string;
  artworkId: string;
  databaseId: string;

  artworkForm: FormGroup;
  isAdmin: boolean;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private formBuilder: FormBuilder,
    private modalService: NgbModal,
    private groupService: GroupService,
    private routeService: RouteService,
    private mediaService: MediaService,
    private artworkService: ArtworkService,
    private messagesService: MessagesService,
    private databaseService: DatabaseService,
    private categoryService: CategoryService,
    private authService: AuthService
  ) {
  }

  ngOnInit() {
    this.code = this.route.snapshot.queryParams.c;
    this.isAdmin = this.authService.isAdmin();

    combineLatest(
      this.route.parent.params,
      this.route.params
    ).subscribe(
      params => {
        this.projectId = params[0]['project_id'];
        this.artworkId = params[1]['artwork_id'];
        this.databaseId = params[0]['database_id'];

        this.artworkForm = this.formBuilder.group({
          name: ['', [Validators.required]],
          code: ['', [Validators.max(9999), Validators.min(1), Validators.required]],
          price: ['', [CustomValidators.priceValidator()]],
          image_cover: ['', [Validators.required]],
          link_banner: [''],
          image_banner: [''],
          group_id: [''],
          route_id: [''],
        });

        forkJoin(
          this.mediaService.getAllMyMedia(this.projectId, null, MediaService.IMAGE_TYPE),
          this.mediaService.getAllMyMedia(this.projectId, null, MediaService.VIDEO_TYPE),
          this.mediaService.getAllMyMedia(this.projectId, null, MediaService.AUDIO_TYPE),
          this.groupService.getAllMyGroups(this.projectId, this.databaseId),
          this.routeService.getAllMyRoutes(this.projectId, this.databaseId),
          this.artworkService.getSingleArtwork(this.projectId, this.databaseId, this.artworkId),
          this.categoryService.getAllMyCategories(this.projectId, this.databaseId)
        ).subscribe(
          () => {
            this.mediaImages = this.mediaService.mediaImages;
            this.mediaVideos = this.mediaService.mediaVideos;
            this.mediaAudios = this.mediaService.mediaAudios;
            this.groups = this.groupService.groups;
            this.routes = this.routeService.routes;
            this.artwork = this.artworkService.artwork;
            this.categories = this.categoryService.categories;
            this.assignedCategories = this.categories.filter(category => this.artwork.categories.find(c => c._id == category._id));

            for (let i = 0; i < this.artwork.categories.length; i++) {
              this.artwork.categories[i].images = this.artwork.categories[i].images.map(i => {
                const media = this.mediaImages.find(m => m._id === i['_id']);
                return media || i;
              });
              this.artwork.categories[i].audios = this.artwork.categories[i].audios.map(i => {
                const media = this.mediaAudios.find(m => m._id === i['_id']);
                return media || i;
              });
              this.artwork.categories[i].videos = this.artwork.categories[i].videos.map(i => {
                const media = this.mediaVideos.find(m => m._id === i['_id']);
                return media || i;
              });
            }
          }, err => this.messagesService.errorToast(err),
          () => this.artworkForm.patchValue({...this.artwork.getObj(), database_id: this.databaseId})
        );
      },
      err => this.messagesService.errorModal(err.toString()),
    );


    this.artworkService.onChange.subscribe(() => {
      this.artwork = this.artworkService.artwork;
    });
    this.categoryService.onChange.subscribe(() => {
      this.categories = this.categoryService.categories;

      this.artwork.categories = this.artwork.categories.filter(category => this.categories.find(c => c._id === category._id));
    })
  };

  onEditCategory(artworkCategory: ArtworkCategory) {
      this.artworkCategoryModal(artworkCategory);
  }

  onSubmit() {
    if (this.artworkForm.controls.code.status === 'INVALID') {
      return;
    }

    const payload = new Artwork(this.artworkForm.getRawValue());

    const obs = [];

    if (payload.group_id)
      obs.push(this.artworkService.addGroup(this.projectId, this.databaseId, this.artworkId, payload.group_id));

    if (payload.route_id)
      obs.push(this.artworkService.addRoute(this.projectId, this.databaseId, this.artworkId, payload.route_id));

    if (!payload.group_id && this.artwork.group_id)
      obs.push(this.artworkService.removeGroup(this.projectId, this.databaseId, this.artworkId, this.artwork.group_id));

    if (!payload.route_id && this.artwork.route_id)
      obs.push(this.artworkService.removeRoute(this.projectId, this.databaseId, this.artworkId, this.artwork.route_id));

    delete payload['categories'];
    obs.push(this.artworkService.patchArtwork(this.projectId, this.databaseId, this.artworkId, payload));

    merge(...obs).subscribe(
      () => {
        this.messagesService.successToast('Artwork updated!', 'Done!');
        this.router.navigate(['/projects/' + this.projectId + '/databases/' + this.databaseId])
          .catch(err => this.messagesService.errorToast(err));
      },
      err => this.messagesService.errorToast(err)
    );
  }

  onSelectImageCover(media: Media) {
    this.artworkForm.patchValue({
      image_cover: media._id
    });
  }

  onSelectImageBanner(media: Media) {
    this.artworkForm.patchValue({
      image_banner: media._id
    });
  }

  onRemoveImageCover(media: Media) {
    this.artworkForm.patchValue({
      image_cover: null
    });
  }

  onRemoveImageBanner(media: Media) {
    this.artworkForm.patchValue({
      image_banner: null
    });
  }

  artworkCategoryModal(artworkCategory?: ArtworkCategory) {
    const modalRef = this.modalService.open(ArtworkCategoryModal, {size: 'lg'});
    modalRef.componentInstance.mediaImages = this.mediaImages;
    modalRef.componentInstance.mediaVideos = this.mediaVideos;
    modalRef.componentInstance.mediaAudios = this.mediaAudios;

    modalRef.componentInstance.databaseId = this.databaseId;
    modalRef.componentInstance.projectId = this.projectId;
    modalRef.componentInstance.artworkId = this.artworkId;

    modalRef.componentInstance.code = this.code;

    modalRef.componentInstance.categories = this.categories;
    modalRef.componentInstance.assignedCategories = this.assignedCategories;

    if (artworkCategory) {
      modalRef.componentInstance.selectedArtworkCategory = artworkCategory;
    }
  }

  onShowModal(artworkCategory?: ArtworkCategory) {
    if (!this.categories.length)
      return this.messagesService.errorToast('Create a section');

    if (this.categories.length === this.artwork.categories.length)
      return this.messagesService.errorToast('There are no available sections');

    this.artworkCategoryModal(artworkCategory);
  }

  onRemoveCategory(artworkId, categoryId) {
    this.messagesService.confirmModal().result
      .then(
        result => {
          if (result) {
            this.artworkService.removeCategory(this.projectId, this.databaseId, artworkId, categoryId).subscribe(
              () => {
                this.assignedCategories.splice(this.assignedCategories.findIndex(c => c["_id"] === categoryId), 1);
                this.messagesService.successToast('Category removed from Artwork.')
              },
              err => this.messagesService.errorToast(err)
            );
          }
        })
      .catch(() => {
      });
  }

  findImageById(id: string) {
    return this.mediaImages.find(m => m._id === id);
  }
  findVideoById(id: string) {
    return this.mediaVideos.find(m => m._id === id);
  }
  findAudioById(id: string) {
    return this.mediaAudios.find(m => m._id === id);
  }
  findCategoryById(id: string) {
    return this.categories.find(c => c._id === id);
  }
}
